import { Component, ErrorInfo, ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import { Button } from "@/components/atoms/button";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (import.meta.env.MODE !== "production") {
       
      console.error("ErrorBoundary caught an error:", error, info);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="flex min-h-screen items-center justify-center p-6 bg-background">
        <div className="max-w-md w-full text-center space-y-4">
          <h1 className="text-2xl font-semibold text-foreground">
            <FormattedMessage
              defaultMessage="Something went wrong" id="o3Scy/"
              description="Error boundary title"
            />
          </h1>
          <p className="text-muted-foreground">
            <FormattedMessage
              defaultMessage="An unexpected error occurred. Please try again or reload the page." id="HG6jpk"
              description="Error boundary description"
            />
          </p>
          <div className="flex justify-center gap-3">
            <Button variant="outline" onClick={this.handleReset}>
              <FormattedMessage defaultMessage="Try again" id="2Dy4QE" description="Retry button" />
            </Button>
            <Button onClick={this.handleReload}>
              <FormattedMessage defaultMessage="Reload page" id="sTItXn" description="Reload button" />
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
